<script setup lang="ts">
import { ref /*, onMounted, onUnmounted*/ } from 'vue'

import Navbar from './components/Navbar.vue'
import GISMap from './components/GISMap.vue'
import AddPlaceWindow from './components/AddPlaceWindow.vue'
import AboutUsWindow from './components/AboutUsWindow.vue'
import AboutProjectWindow from './components/AboutProjectWindow.vue'
import PoemWindow from './components/PoemWindow.vue'
import HowItWorkWindow from './components/HowItWorkWindow.vue'
// import './assets/styles/esri.css'
import CookieBanner from './components/CookieBanner.vue'

import type { PoemAttributes } from './types/types'

// Nedodělané části
// import EventsWindow from './components/EventsWindow.vue';

const showAddPlace = ref(false)
const showPoemWindow = ref(false)
const showProject = ref(false)
const showAbout = ref(false)
const showEvents = ref(false)
const showHowItWorks = ref(false)

const selectedPoemData = ref<PoemAttributes | null>(null)

const mapRef = ref<InstanceType<typeof GISMap> | null>(null)

// Central helper to clear the screen
const closeAllWindows = () => {
  showAddPlace.value = false
  showPoemWindow.value = false
  showProject.value = false
  showAbout.value = false
  showEvents.value = false
  showHowItWorks.value = false
}

// Logic pattern for toggling windows
const toggleAddPlaceWindow = (forceState?: boolean) => {
  if (typeof forceState === 'boolean') {
    if (forceState) closeAllWindows()
    showAddPlace.value = forceState
  } else {
    const willOpen = !showAddPlace.value
    if (willOpen) {
      closeAllWindows()
      selectedPoemData.value = null
      selectedLocation.value = null
    }
    showAddPlace.value = willOpen
  }
}

const toggleAboutWindow = () => {
  const willOpen = !showAbout.value
  if (willOpen) closeAllWindows()
  showAbout.value = willOpen
}

const toggleProjectWindow = () => {
  const willOpen = !showProject.value
  if (willOpen) closeAllWindows()
  showProject.value = willOpen
}

const toggleEventsWindow = () => {
  const willOpen = !showEvents.value
  if (willOpen) closeAllWindows()
  showEvents.value = willOpen
}
const toggleHowItWorksWindow = () => {
  const willOpen = !showHowItWorks.value
  if (willOpen) closeAllWindows()
  showHowItWorks.value = willOpen
}
const handleOpenPoem = (poemData: PoemAttributes) => {
  closeAllWindows()
  selectedPoemData.value = poemData
  showPoemWindow.value = true
}
const handleClosePoem = () => {
  showPoemWindow.value = false
}
const selectedLocation = ref<{ x: number; y: number; ID: string } | null>(null)

const handleLocationSelected = (coords: { x: number; y: number; ID: string }) => {
  selectedLocation.value = coords
  selectedPoemData.value = null
  toggleAddPlaceWindow(true)
}
const handleEditPoem = (data: { x: number; y: number; attributes: any }) => {
  selectedLocation.value = { x: data.x, y: data.y, ID: data.attributes.IDSEZENI }
  selectedPoemData.value = data.attributes
  toggleAddPlaceWindow(true)
}
const handleSaveSuccess = (payload: {
  idsezeni: string
  objectId: number
  attributes: PoemAttributes
}) => {
  if (mapRef.value) {
    mapRef.value.updateLocalGraphic(payload.idsezeni, payload.objectId, payload.attributes)
  }
  showAddPlace.value = false
}
const handleDeleteSuccess = (payload: { idsezeni: string; objectId: number }) => {
  if (mapRef.value) {
    mapRef.value.deleteLocalGraphic(payload.idsezeni, payload.objectId)
  }
  showAddPlace.value = false
}
</script>

<template>
  <div class="app-layout">
    <Navbar
      @open-project="toggleProjectWindow"
      @open-about="toggleAboutWindow"
      @open-events="toggleEventsWindow"
      @toggle-add-place="toggleAddPlaceWindow"
      @close-all="closeAllWindows"
      @open-how-it-works="toggleHowItWorksWindow"
    />

    <GISMap
      ref="mapRef"
      :is-add-place-open="showAddPlace"
      @location-selected="handleLocationSelected"
      @close-all="closeAllWindows"
      @edit-poem="handleEditPoem"
      @open-poem="handleOpenPoem"
      @close-poem="handleClosePoem"
    />

    <Transition name="fade">
      <AddPlaceWindow
        v-if="showAddPlace"
        :location="selectedLocation"
        :existing-data="selectedPoemData"
        @close="showAddPlace = false"
        @save-success="handleSaveSuccess"
        @delete-success="handleDeleteSuccess"
      />
    </Transition>

    <Transition name="fade">
      <PoemWindow
        v-if="showPoemWindow"
        :poem-data="selectedPoemData"
        @close="showPoemWindow = false"
      />
    </Transition>

    <Transition name="fade"
      ><AboutUsWindow v-if="showAbout" @close="showAbout = false"
    /></Transition>
    <Transition name="fade"
      ><AboutProjectWindow v-if="showProject" @close="showProject = false"
    /></Transition>
    <Transition name="fade"
      ><HowItWorkWindow v-if="showHowItWorks" @close="showHowItWorks = false"
    /></Transition>
    <CookieBanner />
    <div class="partner-logos">
      <img
        src="./assets/logos/MKP_3_RADEK.svg"
        alt="Městská knihovna v Praze"
        class="partner-logo"
      />
      <img src="./assets/logos/IPR_logo_1.svg" alt="IPR Praha" class="partner-logo" />
      <img
        src="./assets/logos/logo PML4_na_cerne.svg"
        alt="Praha město literatury"
        class="partner-logo"
      />
    </div>
  </div>
</template>

<style>
/*@import './assets/styles/esri.css';*/
html,
body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  overflow: hidden;
}

.app-layout {
  position: relative;
  width: 100vw;
  height: 100vh;
}

.partner-logos {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 4;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
  pointer-events: none;

  /*background-color: rgba(253, 251, 247, 0.6);*/
  border: 5px solid /*rgba(253, 251, 247, 0.6)*/ transparent;

  background-clip: padding-box;
}

.partner-logo {
  height: 32px;
  width: auto;
  pointer-events: auto;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease; /* Adjust 0.4s to make it faster/slower */
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
:root {
  /* 1. Base colors extracted from your AddPlaceWindow & App components */
  --color-orange: #f2a900;
  --color-orange-dark: #eca600; /* Used in your button borders */
  --color-dark-navy: #1a2432; /* Used for your text */
  --color-gray-light: #cccccc; /* Used for your borders and disabled states */

  /* 2. UI Theme mapped to your app's colors */
  --ui-primary: var(--color-orange);
  --ui-text: var(--color-dark-navy);
  --ui-bg-elevated: #ffffff; /* Your dropdowns use a white background */
  --ui-bg-accented: #f9f9f9;
  --ui-bg-muted: #f5f5f5;
  --ui-text-muted: #888888; /* Used in your disclaimer text */

  /* 3. Calcite Design System Overrides */
  --calcite-font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --calcite-color-brand: var(--ui-primary);
  --calcite-color-text-1: var(--ui-text);
  --calcite-color-text-2: var(--ui-text);
  --calcite-color-text-3: var(--ui-text-muted);
  --calcite-font-size-md: 14px; /* Matches your .text-input font size */
  --calcite-font-weight-semibold: 500;

  /* Note: I fixed the syntax here. You can use color-mix, or just use your dark orange! */
  --calcite-color-brand-hover: var(--color-orange-dark);
  --calcite-font-size: 14px;
  --calcite-color-focus: var(--ui-primary);

  /* 4. ArcGIS Overrides */
  --arcgis-view-color-focus: transparent;
}
</style>
